// Copyright (c) 2025 Goffy. This file belongs to Goffy. All Rights Reserved.

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { refreshAccessToken } from '@/auth/oidc';

export type UserSession = { id: string; forename: string; token: string; idToken?: string | null; refreshToken?: string | null };

type UserContextValue = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
  clear: () => void;
};

const UserContext = createContext<UserContextValue | undefined>(undefined);

const STORAGE_KEY = 'goffy:user';
const EXP_SKEW_SECONDS = 30;
const REFRESH_LEAD_SECONDS = 60;
const REFRESH_RETRY_COOLDOWN_MS = 60_000;
const MIN_REFRESH_DELAY_MS = 1_000;

function decodeJwtExp(token: string): number | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(json) as { exp?: number };
    return typeof parsed.exp === 'number' ? parsed.exp : null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token: string): boolean {
  const exp = decodeJwtExp(token);
  if (!exp) return false;
  const nowSeconds = Date.now() / 1000;
  return exp <= nowSeconds + EXP_SKEW_SECONDS;
}

function loadUser(): UserSession | null {
  try {
    if (typeof localStorage === 'undefined') return null;
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as UserSession;
    if (!parsed?.token) return null;
    if (isTokenExpired(parsed.token) && !parsed.refreshToken) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistUser(user: UserSession | null) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (user) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // ignore storage failures
  }
}

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUserState] = useState<UserSession | null>(() => loadUser());
  const refreshInFlight = useRef(false);
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextRefreshAllowedAtRef = useRef(0);

  const applyUser = (next: UserSession | null) => {
    setUserState(next);
    persistUser(next);
  };

  const refreshSession = useCallback(async (current: UserSession) => {
    if (!current.refreshToken || refreshInFlight.current) return;
    const nowMs = Date.now();
    if (nowMs < nextRefreshAllowedAtRef.current) {
      const retryInMs = Math.max(0, nextRefreshAllowedAtRef.current - nowMs);
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      refreshTimeoutRef.current = setTimeout(() => {
        void refreshSession(current);
      }, retryInMs);
      return;
    }
    refreshInFlight.current = true;
    try {
      const refreshed = await refreshAccessToken(current.refreshToken);
      const refreshedExp = decodeJwtExp(refreshed.accessToken);
      const nowSeconds = Date.now() / 1000;
      if (typeof refreshedExp === 'number' && refreshedExp <= nowSeconds + EXP_SKEW_SECONDS) {
        const skewSeconds = Math.ceil((nowSeconds + EXP_SKEW_SECONDS) - refreshedExp);
        nextRefreshAllowedAtRef.current = Date.now() + REFRESH_RETRY_COOLDOWN_MS;
        console.warn('[auth] Ignoring refreshed token that is already expired.', {
          exp: refreshedExp,
          now: Math.floor(nowSeconds),
          skewSeconds,
          retryInMs: REFRESH_RETRY_COOLDOWN_MS,
        });
        if (refreshTimeoutRef.current) {
          clearTimeout(refreshTimeoutRef.current);
        }
        refreshTimeoutRef.current = setTimeout(() => {
          void refreshSession(current);
        }, REFRESH_RETRY_COOLDOWN_MS);
        return;
      }
      nextRefreshAllowedAtRef.current = 0;
      applyUser({
        ...current,
        token: refreshed.accessToken,
        idToken: refreshed.idToken ?? current.idToken ?? null,
        refreshToken: refreshed.refreshToken ?? current.refreshToken ?? null,
      });
    } catch {
      applyUser(null);
    } finally {
      refreshInFlight.current = false;
    }
  }, []);

  // Auto-clear when token expires to force a fresh login.
  useEffect(() => {
    if (!user) return;
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    const exp = decodeJwtExp(user.token);
    if (!exp) return;
    const nowSeconds = Date.now() / 1000;
    const refreshAtSeconds = exp - REFRESH_LEAD_SECONDS;
    const nowMs = Date.now();
    const earliestAllowedAtMs = Math.max(nowMs + MIN_REFRESH_DELAY_MS, nextRefreshAllowedAtRef.current);
    const requestedRefreshAtMs = refreshAtSeconds * 1000;
    const targetRefreshAtMs = refreshAtSeconds <= nowSeconds + EXP_SKEW_SECONDS
      ? earliestAllowedAtMs
      : Math.max(requestedRefreshAtMs, earliestAllowedAtMs);
    const msUntilRefresh = Math.max(0, targetRefreshAtMs - nowMs);
    refreshTimeoutRef.current = setTimeout(() => {
      void refreshSession(user);
    }, msUntilRefresh);
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
        refreshTimeoutRef.current = null;
      }
    };
  }, [user, refreshSession]);

  const value = useMemo(
    () => ({
      user,
      setUser: (next: UserSession | null) => {
        if (next?.token && isTokenExpired(next.token) && !next.refreshToken) {
          applyUser(null);
          return;
        }
        applyUser(next);
      },
      clear: () => {
        applyUser(null);
      },
    }),
    [user]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useUser() {
  const ctx = useContext(UserContext);
  if (!ctx) {
    throw new Error('useUser must be used within UserProvider');
  }
  return ctx;
}

// Non-visual helper route placeholder for Expo Router.
export default function UserContextRoute() {
  return null;
}
